import { useEffect, useState } from "react";

import { getVenueXPreviewPayload } from "@/data/previewContent";
import { localizedApiHref } from "@/i18n/locale";
import { localizeContent } from "@/i18n/translations";

type PortfolioLink = { label: string; href: string };

export type PortfolioArchivePresentation = {
  hero_title: string;
  intro_kicker: string;
  intro_copy: string;
  intro_heading: string;
  feature_visual: {
    image?: string;
    fallback_url?: string;
    alt?: string;
    position?: string;
  };
  marquee_text: string;
  signals: { label: string; value: string }[];
  detail_eyebrow: string;
  back_label: string;
  cta_kicker: string;
  cta_title: string;
  primary_link: PortfolioLink;
  secondary_link: PortfolioLink;
};

export type PortfolioArchiveItem = {
  id: number | string;
  slug: string;
  url: string;
  title: string;
  projectType: string;
  summary: string;
  clientName: string;
  location: string;
  yearLabel: string;
  bodyHtml: string;
  imageUrl: string;
  imageAlt: string;
  imagePosition: string;
  cardTone: "dark" | "light";
};

export type PortfolioArchiveData = {
  title: string;
  intro: string;
  seoTitle: string;
  searchDescription: string;
  presentation: PortfolioArchivePresentation;
  items: PortfolioArchiveItem[];
};

const fallbackItems: PortfolioArchiveItem[] = [
  ["shipbuilding", "Hellenic Shipbuilding Industry - Germany 2025", "Exhibition presence", "A sharper B2B presence translated into a cleaner stand image, supporting material and clearer memory after the event.", "/static/venuex/live/story-rollup.jpg", "/service/hellenic-shipbuilding-industry-germany-2025/", "dark"],
  ["chic-pink", "Chic Pink 3 years", "Celebration campaign", "Anniversary energy shaped to feel social, polished and recognisable without losing warmth or personality.", "/static/venuex/live/story-party.jpg", "/service/chic-pink-3%CF%87%CF%81%CF%8C%CE%BD%CE%B9%CE%B1/", "light"],
  ["wedding", "Wedding", "Private experience", "A quieter visual rhythm where styling, hospitality and atmosphere move as one authored emotional register.", "/static/venuex/live/story-bright-rose.jpg", "/service/wedding/", "light"],
  ["branding", "Luxury branding systems", "Identity direction", "The same standard carried across logo, typography, website language and every premium touchpoint around the event.", "/static/venuex/content/service-branding.webp", "/our-identity/", "dark"],
  ["growth", "Growth systems in motion", "Digital continuity", "Design, SEO, social, automations and support held inside one authored logic instead of drifting apart over time.", "/static/venuex/content/service-digital-growth.webp", "/service/", "light"],
  ["onsite", "On-site calm under pressure", "Operational execution", "What the client remembers is not just beauty, but how effortless the entire day felt while everything stayed controlled.", "/static/venuex/live/venuex-onsite.jpg", "/contact/", "dark"],
].map(([slug, title, projectType, summary, imageUrl, url, cardTone]) => ({
  id: slug,
  slug,
  title,
  projectType,
  summary,
  imageUrl,
  imageAlt: title,
  imagePosition: "center center",
  url,
  cardTone: cardTone as "dark" | "light",
  clientName: "",
  location: "",
  yearLabel: "",
  bodyHtml: "",
}));

export const DEFAULT_PORTFOLIO_ARCHIVE: PortfolioArchiveData = {
  title: "Selected Work",
  intro: "Selected VenueX work across identity, digital presence and live experiences.",
  seoTitle: "Selected Work",
  searchDescription: "Selected VenueX work across luxury branding, digital experiences and live events.",
  presentation: {
    hero_title: "Showcase",
    intro_kicker: "VenueX portfolio",
    intro_copy: "A selective edit of how VenueX carries the same premium logic across brand, digital presence and the event itself.",
    intro_heading: "Not a gallery dump, but a sharper reading of how presence becomes more desirable, more precise and more memorable when every touchpoint belongs to the same language.",
    feature_visual: {
      fallback_url: "/static/venuex/live/story-experience.jpg",
      alt: "VenueX premium event atmosphere",
      position: "center center",
    },
    marquee_text: "VenueX archive 2026",
    signals: [
      { label: "What this archive reveals", value: "This is not a set of isolated services. It is one authored standard carried from brand identity and digital presence into styling, coordination and guest memory." },
      { label: "What the eye notices", value: "The invitation, the room, the service flow and the final impression all feel cut from the same cloth. That quiet consistency is the real luxury." },
      { label: "Where it opens next", value: "From here the viewer should move naturally into services, inquiry or individual case routes without losing the sense of control, elegance and continuity." },
    ],
    detail_eyebrow: "Project profile",
    back_label: "Back to selected work",
    cta_kicker: "Next move",
    cta_title: "If the standard feels right, the conversation can begin now.",
    primary_link: { label: "Contact VenueX", href: "/contact/" },
    secondary_link: { label: "Explore services", href: "/service/" },
  },
  items: fallbackItems,
};

type PortfolioArchiveApiResponse = {
  title: string;
  intro: string;
  seo_title: string;
  search_description: string;
  presentation: PortfolioArchivePresentation;
  items: Array<{
    id: number;
    slug: string;
    url: string;
    title: string;
    project_type: string;
    summary: string;
    client_name: string;
    location: string;
    year_label: string;
    body_html: string;
    image_url: string;
    image_alt: string;
    image_position: string;
    card_tone: "dark" | "light";
  }>;
};

function mapPortfolioArchive(response: PortfolioArchiveApiResponse): PortfolioArchiveData {
  return localizeContent({
    title: response.title,
    intro: response.intro,
    seoTitle: response.seo_title,
    searchDescription: response.search_description,
    presentation: response.presentation,
    items: response.items.map((item) => ({
      id: item.id,
      slug: item.slug,
      url: item.url,
      title: item.title,
      projectType: item.project_type,
      summary: item.summary,
      clientName: item.client_name,
      location: item.location,
      yearLabel: item.year_label,
      bodyHtml: item.body_html,
      imageUrl: item.image_url,
      imageAlt: item.image_alt,
      imagePosition: item.image_position,
      cardTone: item.card_tone,
    })),
  });
}

export function usePortfolioArchive() {
  const previewContent = getVenueXPreviewPayload<PortfolioArchiveApiResponse>("portfolio-archive");
  const [data, setData] = useState(() =>
    previewContent ? mapPortfolioArchive(previewContent) : localizeContent(DEFAULT_PORTFOLIO_ARCHIVE),
  );

  useEffect(() => {
    if (previewContent) return;

    const controller = new AbortController();
    fetch(localizedApiHref("/api/portfolio/"), {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Portfolio archive unavailable");
        return response.json() as Promise<PortfolioArchiveApiResponse>;
      })
      .then((response) => setData(mapPortfolioArchive(response)))
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setData(localizeContent(DEFAULT_PORTFOLIO_ARCHIVE));
        }
      });

    return () => controller.abort();
  }, [previewContent]);

  return data;
}
